import React, { useState, useEffect } from "react";
import { Users, Building, GraduationCap, BookOpen, Shield, Heart, Mail, ExternalLink, Award, Globe } from "lucide-react";

const StakeHolders = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [animated, setAnimated] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    // Trigger animations after component mounts
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Stakeholder data organized by categories
  const stakeholders = [
    {
      id: 1,
      name: "Federal Government of Nigeria",
      role: "Oversight and Funding Authority",
      category: "government",
      description: "Provides oversight, policy direction, and funding for the institution as a federal polytechnic.",
      responsibilities: ["Policy formulation", "Funding allocation", "Strategic oversight"],
      icon: <Building className="w-8 h-8" />,
      contactEmail: "info@fedpolybida.edu.ng",
      learnMoreUrl: "/about/government-relations"
    },
    {
      id: 2,
      name: "Governing Council",
      role: "Strategic Governance Body",
      category: "governance",
      description: "Appointed by the government to provide overall governance and strategic direction for the polytechnic.",
      responsibilities: ["Strategic planning", "Policy approval", "Institutional oversight"],
      icon: <Shield className="w-8 h-8" />,
      contactEmail: "council@fedpolybida.edu.ng",
      learnMoreUrl: "/about/governing-council"
    },
    {
      id: 3,
      name: "TETFund",
      role: "Tertiary Education Trust Fund",
      category: "government",
      description: "Provides funding support for infrastructure, research, and academic development in tertiary institutions.",
      responsibilities: ["Infrastructure development", "Research funding", "Academic staff development", "Library funding"],
      icon: <Award className="w-8 h-8" />,
      contactEmail: "tetfund@fedpolybida.edu.ng",
      learnMoreUrl: "/about/tetfund"
    },
    {
      id: 4,
      name: "NCC",
      role: "National Communications Commission",
      category: "regulatory",
      description: "Regulatory body responsible for setting standards and coordinating the development of polytechnic education in Nigeria.",
      responsibilities: ["Promoting ICT Use", "Quality assurance", "Standard maintenance", "Consumer Protection"],
      icon: <Globe className="w-8 h-8" />,
      contactEmail: "ncc@fedpolybida.edu.ng",
      learnMoreUrl: "/about/ncc"
    },
    {
      id: 5,
      name: "Management Team",
      role: "Institutional Leadership",
      category: "management",
      description: "Headed by the Rector, this team manages the day-to-day operations and implementation of institutional policies.",
      responsibilities: ["Administrative management", "Policy implementation", "Academic leadership"],
      icon: <Users className="w-8 h-8" />,
      contactEmail: "management@fedpolybida.edu.ng",
      learnMoreUrl: "/about/management"
    },
    {
      id: 6,
      name: "Academic Staff",
      role: "Teaching and Research Faculty",
      category: "academic",
      description: "Lecturers, researchers, and academic support staff who deliver quality education and conduct research.",
      responsibilities: ["Teaching delivery", "Research activities", "Student mentorship"],
      icon: <BookOpen className="w-8 h-8" />,
      contactEmail: "academics@fedpolybida.edu.ng",
      learnMoreUrl: "/about/academic-staff"
    },
    {
      id: 7,
      name: "Student Body",
      role: "Primary Beneficiaries",
      category: "students",
      description: "Current students pursuing various academic programs across different departments and schools.",
      responsibilities: ["Academic pursuit", "Campus participation", "Community engagement"],
      icon: <GraduationCap className="w-8 h-8" />,
      contactEmail: "students@fedpolybida.edu.ng",
      learnMoreUrl: "/students"
    },
    {
      id: 8,
      name: "NBTE",
      role: "Regulatory Body",
      category: "regulatory",
      description: "National Board for Technical Education - Approves courses and ensures the quality of technical education.",
      responsibilities: ["Program accreditation", "Quality assurance", "Standards maintenance"],
      icon: <Shield className="w-8 h-8" />,
      contactEmail: "nbte@fedpolybida.edu.ng",
      learnMoreUrl: "/about/nbte"
    },
    {
      id: 9,
      name: "Bida Host Community",
      role: "Local Stakeholders",
      category: "community",
      description: "The local community in Bida, including traditional rulers like the Etsu Nupe, with stake in the polytechnic's impact.",
      responsibilities: ["Community relations", "Local development", "Cultural integration"],
      icon: <Heart className="w-8 h-8" />,
      contactEmail: "community@fedpolybida.edu.ng",
      learnMoreUrl: "/about/community"
    },
    {
      id: 10,
      name: "Alumni Association",
      role: "Graduate Network",
      category: "alumni",
      description: "Former students who maintain connection with the institution and contribute to its development.",
      responsibilities: ["Networking", "Mentorship", "Institutional support"],
      icon: <Users className="w-8 h-8" />,
      contactEmail: "alumni@fedpolybida.edu.ng",
      learnMoreUrl: "/alumni"
    },
    {
      id: 11,
      name: "Partner Institutions",
      role: "Academic Collaborators",
      category: "partners",
      description: "Other educational institutions like FUTMINNA that collaborate for academic affiliation and student support.",
      responsibilities: ["Academic collaboration", "Resource sharing", "Joint programs"],
      icon: <Building className="w-8 h-8" />,
      contactEmail: "partnerships@fedpolybida.edu.ng",
      learnMoreUrl: "/about/partnerships"
    },
    {
      id: 12,
      name: "Academic Committees",
      role: "Decision-making Bodies",
      category: "committees",
      description: "Various committees including Rector's Advisory Committee and academic boards that play crucial roles in operations.",
      responsibilities: ["Policy development", "Academic planning", "Quality assurance"],
      icon: <Users className="w-8 h-8" />,
      contactEmail: "committees@fedpolybida.edu.ng",
      learnMoreUrl: "/about/committees"
    }
  ];

  // Category information with better contrast colors
  const categories = [
    { id: 'all', name: 'All Stakeholders', color: 'bg-gray-200 text-gray-900' },
    { id: 'government', name: 'Government', color: 'bg-blue-200 text-blue-900' },
    { id: 'governance', name: 'Governance', color: 'bg-purple-200 text-purple-900' },
    { id: 'management', name: 'Management', color: 'bg-green-200 text-green-900' },
    { id: 'academic', name: 'Academic', color: 'bg-red-200 text-red-900' },
    { id: 'students', name: 'Students', color: 'bg-amber-200 text-amber-900' },
    { id: 'regulatory', name: 'Regulatory', color: 'bg-indigo-200 text-indigo-900' },
    { id: 'community', name: 'Community', color: 'bg-pink-200 text-pink-900' },
    { id: 'alumni', name: 'Alumni', color: 'bg-teal-200 text-teal-900' },
    { id: 'partners', name: 'Partners', color: 'bg-orange-200 text-orange-900' },
    { id: 'committees', name: 'Committees', color: 'bg-cyan-200 text-cyan-900' }
  ];

  // Filter stakeholders based on active category
  const filteredStakeholders = activeCategory === 'all' 
    ? stakeholders 
    : stakeholders.filter(stakeholder => stakeholder.category === activeCategory);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <section 
      className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 overflow-hidden"
      aria-labelledby="stakeholders-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className={`transition-all duration-700 transform ${animated && !prefersReducedMotion ? 'translate-y-0 opacity-100' : prefersReducedMotion ? 'opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h1 id="stakeholders-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Stakeholders
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              The diverse groups and individuals who contribute to the success and development of Federal Polytechnic Bida
            </p>
          </div>
        </div>

        {/* Category Filter */}
        <div 
          className="mb-12"
          role="group"
          aria-label="Filter stakeholders by category"
        >
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                aria-pressed={activeCategory === category.id}
                aria-label={`Filter by ${category.name}`}
                className={`px-3 sm:px-4 py-2 rounded-full font-medium transition-all text-xs sm:text-sm transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  activeCategory === category.id 
                    ? 'bg-blue-700 text-white shadow-lg' 
                    : `${category.color} hover:shadow-md`
                } ${animated && !prefersReducedMotion ? 'translate-y-0 opacity-100' : prefersReducedMotion ? 'opacity-100' : 'translate-y-10 opacity-0'}`}
                style={!prefersReducedMotion ? { transitionDelay: `${index * 30}ms` } : {}}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {filteredStakeholders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No stakeholders found in this category.</p>
          </div>
        )}

        {/* Stakeholders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
          {filteredStakeholders.map((stakeholder, index) => {
            const category = categories.find(cat => cat.id === stakeholder.category);
            return (
              <article 
                key={stakeholder.id} 
                className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-1 ${
                  animated && !prefersReducedMotion ? 'translate-y-0 opacity-100' : prefersReducedMotion ? 'opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={!prefersReducedMotion ? { transitionDelay: `${index * 50}ms` } : {}}
              >
                <div className={`p-4 sm:p-6 ${category.color}`}>
                  <div className="flex items-center">
                    <div className="mr-3 sm:mr-4" aria-hidden="true">
                      {stakeholder.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-bold truncate" title={stakeholder.name}>
                        {stakeholder.name}
                      </h3>
                      <p className="text-xs sm:text-sm opacity-90 truncate" title={stakeholder.role}>
                        {stakeholder.role}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <p className="text-sm sm:text-base text-gray-600 mb-4 line-clamp-3">
                    {stakeholder.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                      Key Responsibilities:
                    </h4>
                    <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                      {stakeholder.responsibilities.map((resp, idx) => (
                        <li 
                          key={idx} 
                          className="flex items-start transition-transform duration-300 hover:translate-x-1"
                        >
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0" aria-hidden="true"></span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                    <a 
                      href={`mailto:${stakeholder.contactEmail}`}
                      aria-label={`Send email to ${stakeholder.name}`}
                      className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 flex items-center transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                    >
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1" aria-hidden="true" />
                      Contact
                    </a>
                    <a 
                      href={stakeholder.learnMoreUrl}
                      aria-label={`Learn more about ${stakeholder.name}`}
                      className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 flex items-center transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                    >
                      Learn More
                      <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-1" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Stakeholder Relationships Diagram */}
        <div className={`bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-12 transition-all duration-700 ${
          animated && !prefersReducedMotion ? 'translate-y-0 opacity-100' : prefersReducedMotion ? 'opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-8">
            Stakeholder Relationships
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4">Internal Stakeholders</h3>
              <div className="space-y-3">
                {['Management Team', 'Academic Staff', 'Student Body', 'Academic Committees'].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center p-3 bg-blue-50 rounded-lg transition-transform duration-300 hover:translate-x-2"
                    style={!prefersReducedMotion ? { transitionDelay: `${index * 50}ms` } : {}}
                  >
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3 flex-shrink-0" aria-hidden="true"></div>
                    <span className="text-sm sm:text-base text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4">External Stakeholders</h3>
              <div className="space-y-3">
                {['Federal Government', 'Governing Council', 'TETFund', 'NCC', 'NBTE', 'Host Community', 'Partner Institutions', 'Alumni Association'].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center p-3 bg-green-50 rounded-lg transition-transform duration-300 hover:translate-x-2"
                    style={!prefersReducedMotion ? { transitionDelay: `${index * 50}ms` } : {}}
                  >
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 flex-shrink-0" aria-hidden="true"></div>
                    <span className="text-sm sm:text-base text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className={`bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 sm:p-8 text-center text-white transition-all duration-700 ${
          animated && !prefersReducedMotion ? 'translate-y-0 opacity-100' : prefersReducedMotion ? 'opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Engage With Our Community</h2>
          <p className="text-sm sm:text-base mb-6 max-w-2xl mx-auto px-4">
            Interested in partnering with us or learning more about how you can contribute to our institution's growth?
          </p>
          <a 
            href="/contact"
            className="inline-block bg-white text-blue-700 font-bold py-3 px-6 sm:px-8 rounded-lg hover:bg-blue-50 transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 text-sm sm:text-base"
          >
            Get In Touch
          </a>
        </div>
      </div>
    </section>
  );
};

export default StakeHolders;